import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { Card } from '@/components/ui'
import { Search } from 'lucide-react'

interface PartnerGroup {
  partnerNumber: number
  studiosCount: number
  citiesText: string
  cities: string[]
  personsFio: string
}

function buildPartnerGroupsFromStudios(studios: ReturnType<typeof useStudioStore.getState>['studios']): PartnerGroup[] {
  const visibleStatuses = new Set(['OPEN', 'PRE-OPEN', 'SUSPENDED', 'SERVICE'])
  const fallbackMap = new Map<number, { cities: Set<string>, studiosCount: number }>()

  for (const studio of studios) {
    const studioStatus = studio.status_code || 'OPEN'
    if (!visibleStatuses.has(studioStatus)) continue
    if (studio.partner_number === null || studio.partner_number === undefined) continue

    const partnerNumber = Number(studio.partner_number)
    if (Number.isNaN(partnerNumber)) continue

    const current = fallbackMap.get(partnerNumber) || { cities: new Set<string>(), studiosCount: 0 }
    current.studiosCount += 1
    if (studio.city?.city_name) current.cities.add(studio.city.city_name)
    fallbackMap.set(partnerNumber, current)
  }

  return Array.from(fallbackMap.entries()).map(([partnerNumber, value]) => {
    const cities = Array.from(value.cities)
    return {
      partnerNumber,
      studiosCount: value.studiosCount,
      citiesText: cities.join(', '),
      cities,
      personsFio: ''
    }
  }).sort((a, b) => a.partnerNumber - b.partnerNumber)
}

export function PartnersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { studios, setSelectedPartnerNumber, setPartners } = useStudioStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [partnersData, setPartnersData] = useState<PartnerGroup[]>([])
  const [error, setError] = useState<string | null>(null)

  const handlePartnerOpen = (partnerNumber: number) => {
    setSelectedPartnerNumber(partnerNumber)
    const partnerStudios = studios.filter((studio) => Number(studio.partner_number) === partnerNumber)
    const seniorOnly = partnerStudios.length > 0 && partnerStudios.every(
      (studio) => studio.access_kind === 'senior_teacher' || studio.role === 'senior_teacher' || studio.role === 'teacher'
    )
    const pedagogyAccess = partnerStudios.find(
      (studio) => studio.role === 'manager' || studio.role === 'owner' || studio.role === 'senior_teacher'
        || studio.access_kind === 'management_company' || studio.access_kind === 'senior_teacher'
    )
    if (seniorOnly || pedagogyAccess?.role === 'manager') {
      const studioId = pedagogyAccess?.studio_internal_id ?? partnerStudios[0].studio_internal_id
      navigate(`/partners/${partnerNumber}/studios/${studioId}/teachers`)
      return
    }
    navigate(`/partners/${partnerNumber}/analytics`)
  }

  useEffect(() => {
    const loadPartners = async () => {
      if (!user?.id) {
        setPartnersData([])
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const { data, error: partnersError } = await withAuthRecovery(() =>
          supabase.rpc('get_user_partners_with_access_v2', { p_user_id: user.id })
        )

        if (partnersError) {
          throw partnersError
        }

        const mapped: PartnerGroup[] = ((data || []) as Array<{
          partner_number: number | string
          persons_fio: string | null
          cities: string | null
          studios_count: number | null
        }>).map((item) => {
          const partnerNumber = Number(item.partner_number)
          const citiesText = (item.cities || '').trim()
          const cities = citiesText ? citiesText.split(',').map((city) => city.trim()).filter(Boolean) : []
          const personsFio = (item.persons_fio || '').trim()

          return {
            partnerNumber,
            studiosCount: item.studios_count || 0,
            citiesText,
            cities,
            personsFio
          }
        }).filter((item) => !Number.isNaN(item.partnerNumber))

        const fallback = buildPartnerGroupsFromStudios(studios)
        const merged = new Map<number, PartnerGroup>(mapped.map((item) => [item.partnerNumber, item]))
        for (const item of fallback) {
          if (!merged.has(item.partnerNumber)) merged.set(item.partnerNumber, item)
        }

        if (mapped.length === 0 && fallback.length > 0) {
          console.warn('⚠️ PartnersPage: RPC returned no partners, using fallback from loaded studios')
          setError('Франчайзи не пришли из RPC, показан резервный список по доступным студиям')
        }

        setPartnersData([...merged.values()].sort((a, b) => a.partnerNumber - b.partnerNumber))
      } catch (err) {
        console.error('❌ PartnersPage: error loading partners, using fallback from studios:', err)
        setError('Не удалось загрузить франчайзи из RPC, показан резервный список')
        setPartnersData(buildPartnerGroupsFromStudios(studios))
      } finally {
        setLoading(false)
      }
    }

    loadPartners()
  }, [user?.id, studios])

  const filteredPartnerGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return partnersData
    }

    return partnersData.filter((partner) => {
      const partnerNumberMatch = String(partner.partnerNumber).includes(query)
      const fioMatch = partner.personsFio.toLowerCase().includes(query)
      const cityMatch = partner.citiesText.toLowerCase().includes(query)
      return partnerNumberMatch || fioMatch || cityMatch
    })
  }, [partnersData, searchQuery])

  useEffect(() => {
    setPartners(
      partnersData.map((item) => ({
        partner_number: item.partnerNumber,
        label: item.personsFio ? `${item.personsFio} (№ ${item.partnerNumber})` : `Партнер (№ ${item.partnerNumber})`,
        studios_count: item.studiosCount,
        city_list: item.cities,
        persons_fio: item.personsFio
      }))
    )
  }, [partnersData, setPartners])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-6">
          <p className="text-muted-foreground">Загрузка партнеров...</p>
        </Card>
      </div>
    )
  }

  if (studios.length === 0) {
    return <Navigate to="/no-access" replace />
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-3xl mx-auto space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Поиск по ФИО, номеру партнера или городу"
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        {error && (
          <Card className="p-3">
            <p className="text-xs text-amber-700">{error}</p>
          </Card>
        )}
        {filteredPartnerGroups.length > 0 ? (
          filteredPartnerGroups.map((partner) => (
            <button
              key={partner.partnerNumber}
              onClick={() => handlePartnerOpen(partner.partnerNumber)}
              className="w-full text-left"
            >
              <Card className="p-4 hover:bg-muted/40 transition-colors">
                <h3 className="text-lg font-semibold text-foreground">
                  {partner.personsFio ? partner.personsFio : 'Партнер'} (№ {partner.partnerNumber})
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {(partner.cities.length > 0 ? partner.cities.join(', ') : 'Города не указаны')}
                  {' '}
                  ({partner.studiosCount}{' '}
                  {partner.studiosCount === 1 ? 'студия' : partner.studiosCount < 5 ? 'студии' : 'студий'})
                </p>
              </Card>
            </button>
          ))
        ) : (
          <Card className="text-center p-6">
            <p className="text-muted-foreground">
              {searchQuery ? 'По вашему запросу партнеры не найдены' : 'Нет доступных партнеров с привязкой к студиям'}
            </p>
          </Card>
        )}
      </div>
    </div>
  )
}
